const AUTH_KEYS = ['token', 'refreshToken', 'user', 'tenantSchema', 'studentData', 'selectedStudentCode', 'selectedStudentId'] as const;

const migrateAuthFromSessionStorage = (): void => {
  AUTH_KEYS.forEach((key) => {
    const sessionValue = sessionStorage.getItem(key);
    if (sessionValue && !localStorage.getItem(key)) {
      localStorage.setItem(key, sessionValue);
    }
    sessionStorage.removeItem(key);
  });
};

/**
 * Get authentication token from storage.
 * Uses localStorage so auth is shared across browser tabs.
 */
export const getAuthToken = (): string | null => {
  migrateAuthFromSessionStorage();
  return localStorage.getItem('token');
};

/**
 * Get user data from storage and clean it.
 * Removes duplicate 'role' field before returning.
 */
export const getUserData = (): string | null => {
  migrateAuthFromSessionStorage();
  const userString = localStorage.getItem('user');

  if (!userString) return null;

  try {
    const userData = JSON.parse(userString);
    const { role, ...cleanUserData } = userData;
    return JSON.stringify(cleanUserData);
  } catch (error) {
    return userString;
  }
};

/**
 * Get refresh token from storage.
 */
export const getRefreshToken = (): string | null => {
  migrateAuthFromSessionStorage();
  return localStorage.getItem('refreshToken');
};

/**
 * Get tenant schema from storage.
 */
export const getTenantSchema = (): string | null => {
  migrateAuthFromSessionStorage();
  return localStorage.getItem('tenantSchema');
};

/**
 * Store authentication token (shared across tabs via localStorage).
 */
export const setAuthToken = (token: string): void => {
  localStorage.setItem('token', token);
  sessionStorage.removeItem('token');
};

/**
 * Store refresh token (shared across tabs via localStorage).
 */
export const setRefreshToken = (refreshToken: string): void => {
  localStorage.setItem('refreshToken', refreshToken);
  sessionStorage.removeItem('refreshToken');
};

/**
 * Store user data and clean it.
 * Removes duplicate 'role' field before storing.
 */
export const setUserData = (userData: string): void => {
  try {
    const parsedData = JSON.parse(userData);
    const { role, ...cleanUserData } = parsedData;
    localStorage.setItem('user', JSON.stringify(cleanUserData));
  } catch (error) {
    localStorage.setItem('user', userData);
  }
  sessionStorage.removeItem('user');
};

/**
 * Store tenant schema (shared across tabs via localStorage).
 */
export const setTenantSchema = (schema: string): void => {
  localStorage.setItem('tenantSchema', schema);
  sessionStorage.removeItem('tenantSchema');
};

/**
 * Clear all authentication data from both storages.
 */
export const clearAuthData = (): void => {
  AUTH_KEYS.forEach((key) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });

  localStorage.removeItem('studentClassId');
  localStorage.removeItem('studentSessionId');

  clearSchoolFormDraft();
  clearApplicationFormDraft();
};

/**
 * Save school form draft data to localStorage
 */
export const saveSchoolFormDraft = (formData: any): void => {
  try {
    localStorage.setItem('schoolFormDraft', JSON.stringify(formData));
  } catch (error) {
  }
};

/**
 * Get school form draft data from localStorage
 */
export const getSchoolFormDraft = (): any | null => {
  try {
    const draft = localStorage.getItem('schoolFormDraft');
    return draft ? JSON.parse(draft) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Clear school form draft data from localStorage
 */
export const clearSchoolFormDraft = (): void => {
  localStorage.removeItem('schoolFormDraft');
};

/**
 * Save application form draft data to localStorage
 */
export const saveApplicationFormDraft = (formData: any): void => {
  try {
    localStorage.setItem('applicationFormDraft', JSON.stringify(formData));
  } catch (error) {
  }
};

/**
 * Get application form draft data from localStorage
 */
export const getApplicationFormDraft = (): any | null => {
  try {
    const draft = localStorage.getItem('applicationFormDraft');
    return draft ? JSON.parse(draft) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Clear application form draft data from localStorage
 */
export const clearApplicationFormDraft = (): void => {
  localStorage.removeItem('applicationFormDraft');
};

/**
 * Save student data to storage (shared across tabs).
 */
export const setStudentData = (studentData: any): void => {
  try {
    localStorage.setItem('studentData', JSON.stringify(studentData));
    sessionStorage.removeItem('studentData');
  } catch (error) {
  }
};

/**
 * Get student data from storage
 */
export const getStudentData = (): any | null => {
  migrateAuthFromSessionStorage();
  try {
    const studentString = localStorage.getItem('studentData');
    return studentString ? JSON.parse(studentString) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Clear student data from storage
 */
export const clearStudentData = (): void => {
  sessionStorage.removeItem('studentData');
  localStorage.removeItem('studentData');
};

export const setSchoolData = (schoolData: any): void => {
  try {
    localStorage.setItem('schoolData', JSON.stringify(schoolData));
  } catch (error) {
    // Handle storage errors silently
  }
};

export const getSchoolData = (): any | null => {
  try {
    const schoolString = localStorage.getItem('schoolData');
    return schoolString ? JSON.parse(schoolString) : null;
  } catch (error) {
    return null;
  }
};

export const clearSchoolData = (): void => {
  localStorage.removeItem('schoolData');
};

/**
 * Save selected student code (shared across tabs).
 */
export const setSelectedStudentCode = (code: string): void => {
  localStorage.setItem('selectedStudentCode', code);
  sessionStorage.removeItem('selectedStudentCode');
};

/**
 * Get selected student code from storage
 */
export const getSelectedStudentCode = (): string | null => {
  migrateAuthFromSessionStorage();
  return localStorage.getItem('selectedStudentCode');
};

/**
 * Clear selected student code from storage
 */
export const clearSelectedStudentCode = (): void => {
  sessionStorage.removeItem('selectedStudentCode');
  localStorage.removeItem('selectedStudentCode');
};

/**
 * Save selected student ID (shared across tabs).
 */
export const setSelectedStudentId = (id: number | string): void => {
  localStorage.setItem('selectedStudentId', String(id));
  sessionStorage.removeItem('selectedStudentId');
};

/**
 * Get selected student ID from storage
 */
export const getSelectedStudentId = (): number | null => {
  migrateAuthFromSessionStorage();
  const val = localStorage.getItem('selectedStudentId');
  return val ? parseInt(val, 10) : null;
};

/**
 * Clear selected student ID from storage
 */
export const clearSelectedStudentId = (): void => {
  sessionStorage.removeItem('selectedStudentId');
  localStorage.removeItem('selectedStudentId');
};

/** Keys watched for cross-tab auth sync via the storage event. */
export const AUTH_SYNC_STORAGE_KEYS = ['user', 'tenantSchema'] as const;
